import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import getChipTypesOptions from '@lib/components/Chip/utils/getChipTypesOptions';
import WidgetTemplateCreateActions from './WidgetTemplateCreateActions';
import getWidgetTemplateCreateFormFields, {
  getWidgetKindOptions,
} from './utils/getWidgetTemplateCreateFormFields';
import WidgetTemplatePreviewModal from '../WidgetTemplatePreviewModal';

export interface WidgetTemplateCreateProps {
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: Values) => void;
}

export default function WidgetTemplateCreate({
  loading,
  onCancel,
  onSubmit,
}: WidgetTemplateCreateProps) {
  const { t } = useTranslation();
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Initialize preview values with defaults
  const defaultValues = useMemo(() => {
    const kindOptions = getWidgetKindOptions(t);
    return {
      kind: kindOptions[0] || null,
      title: '',
      description: '',
      isPrivate: false,
      backgroundColor: getChipTypesOptions({ t })[0],
      content: '',
      link: '',
      image: null,
      iconImage: null,
    };
  }, [t]);

  const [previewFormValues, setPreviewFormValues] =
    useState<Values>(defaultValues);

  // Update preview form values on any field change
  const handleFieldChange = useCallback((values: Values) => {
    setPreviewFormValues(values);
  }, []);

  // Preview handler - opens modal with already synced form values
  const handlePreviewClick = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
  }, []);

  const fields = useMemo(
    () =>
      getWidgetTemplateCreateFormFields({
        t,
        onPreviewClick: handlePreviewClick,
        onFieldChange: handleFieldChange,
      }),
    [t, handlePreviewClick, handleFieldChange],
  );

  const actionsComponent: (actionsProps: ActionsProps) => React.ReactElement = (
    actionsProps,
  ) => (
    <WidgetTemplateCreateActions
      {...actionsProps}
      onCancel={onCancel}
      loading={loading}
    />
  );

  return (
    <>
      <PageHeader title={t('home.create-widget-template')} />
      <PageBody>
        <DashboardContentMaxWidth>
          <WhiteBox verticalPaddings="large">
            <Form
              actions={actionsComponent}
              defaultValues={defaultValues}
              fields={fields}
              formId="widget-template-create-form"
              loading={loading}
              onSubmit={onSubmit}
            />
          </WhiteBox>
        </DashboardContentMaxWidth>
      </PageBody>
      <WidgetTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={handleClosePreviewModal}
        formValues={previewFormValues}
      />
    </>
  );
}
